import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const GRADE_COLOR = {
  'A+': '#00ff87', 'A': '#00e676', 'A-': '#69f0ae',
  'B+': '#40c4ff', 'B': '#29b6f6', 'B-': '#81d4fa',
  'C+': '#ffca28', 'C': '#ffd54f', 'C-': '#ffe082',
  'D':  '#ff7043', 'F': '#ef5350',
};

const POS_COLOR = {
  QB: 'var(--accent2)', RB: '#00d4aa', WR: 'var(--accent)', TE: '#ce93d8',
};

function GradeBadge({ grade, size = 'md' }) {
  const color = GRADE_COLOR[grade] || '#888';
  const sz = size === 'lg' ? { fontSize: 28, width: 56, height: 56 }
           : size === 'sm' ? { fontSize: 13, width: 32, height: 32 }
           : { fontSize: 18, width: 42, height: 42 };
  return (
    <div style={{
      ...sz,
      borderRadius: '50%',
      background: `${color}22`,
      border: `2px solid ${color}`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 800,
      fontFamily: 'monospace',
      flexShrink: 0,
    }}>
      {grade}
    </div>
  );
}

function PosBadge({ pos }) {
  return (
    <span style={{
      background: `${POS_COLOR[pos] || '#555'}33`,
      color: POS_COLOR[pos] || '#aaa',
      border: `1px solid ${POS_COLOR[pos] || '#555'}66`,
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      padding: '1px 5px',
      letterSpacing: 1,
    }}>
      {pos}
    </span>
  );
}

function PlayerRow({ player }) {
  const displayName = player.name || player.label || `Player ${player.player_id || ''}`;
  const tierColor = player.tier === 1 ? 'var(--green)' : player.tier === 2 ? '#40c4ff' :
                    player.tier === 3 ? '#ffca28' : '#888';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 0', borderBottom: '1px solid #ffffff08',
    }}>
      <PosBadge pos={player.position} />
      <span style={{ flex: 1, fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
        {displayName}
      </span>
      {player.age && (
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Age {player.age}</span>
      )}
      {player.tier != null && (
        <span style={{ fontSize: 11, color: tierColor, fontWeight: 600 }}>
          T{player.tier}{player.tier_label ? ` ${player.tier_label}` : ''}
        </span>
      )}
    </div>
  );
}

function PickRow({ pick }) {
  const displayLabel = pick.label || `${pick.season || pick.year || '?'} Round ${pick.round || '?'}${pick.slot ? ` (slot ${pick.slot})` : ''}`;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 0', borderBottom: '1px solid #ffffff08',
    }}>
      <span style={{
        background: '#ffffff15', color: 'var(--text2)', borderRadius: 4,
        fontSize: 10, fontWeight: 700, padding: '1px 5px',
      }}>PICK</span>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>
        {displayLabel}
      </span>
    </div>
  );
}

function TradeCard({ txn }) {
  const [expanded, setExpanded] = useState(false);
  const g = txn.trade_grade;

  // Fallback: grade data missing or malformed — show a minimal trade stub
  if (!g || !g.sides) {
    const date = txn.status_updated
      ? new Date(txn.status_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Unknown date';
    const managers = txn.managers || [];
    return (
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
        marginBottom: 12, padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            {managers.join(' ↔ ') || 'Trade'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{date}</div>
        </div>
        <span style={{
          background: '#ffffff11', color: 'var(--text3)', border: '1px solid #ffffff22',
          borderRadius: 20, fontSize: 11, fontWeight: 600, padding: '3px 10px',
        }}>
          Grade unavailable
        </span>
      </div>
    );
  }

  const rids = g.roster_ids || [];
  const sideA = g.sides[rids[0]] || {};
  const sideB = g.sides[rids[1]] || {};
  const atTime = g.at_time || {};
  const aged = g.aged || {};

  const winnerRid = atTime.winner_roster_id;
  const winnerName = winnerRid
    ? (g.sides[winnerRid] || {}).manager || 'Unknown'
    : null;

  const agingEmoji = aged.aging_emoji || '➡️';
  const agingVerdict = aged.aging_verdict || 'Holding Steady';

  const date = g.trade_date
    ? new Date(g.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Unknown date';

  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      marginBottom: 12,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--bg4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="trade-card-header"
        style={{
          padding: '14px 18px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 14,
        }}
      >
        {/* Grades */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <GradeBadge grade={atTime.grade_a || '?'} />
          <span style={{ color: 'var(--text3)', fontSize: 18, fontWeight: 300 }}>↔</span>
          <GradeBadge grade={atTime.grade_b || '?'} />
        </div>

        {/* Trade summary */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            {sideA.manager || 'Team A'} ↔ {sideB.manager || 'Team B'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{date}</div>
        </div>

        {/* Verdict pill */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, maxWidth: 130 }}>
          {winnerName ? (
            <span style={{
              background: '#00ff8722', color: 'var(--green)',
              border: '1px solid #00ff8744',
              borderRadius: 20, fontSize: 11, fontWeight: 700,
              padding: '3px 10px', whiteSpace: 'nowrap',
            }}>
              🏆 {winnerName} wins
            </span>
          ) : (
            <span style={{
              background: '#ffffff11', color: 'var(--text2)',
              border: '1px solid #ffffff22',
              borderRadius: 20, fontSize: 11, fontWeight: 600,
              padding: '3px 10px',
            }}>
              ⚖️ Fair Trade
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            {agingEmoji} {agingVerdict}
          </span>
        </div>

        <span style={{ color: 'var(--text3)', fontSize: 18, transform: expanded ? 'rotate(90deg)' : 'none', transition: '0.2s' }}>›</span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {/* Grade narrative */}
          {g.narrative && (
            <div style={{
              padding: '12px 18px',
              borderBottom: '1px solid var(--border)',
              fontSize: 13, color: 'var(--text2)', lineHeight: 1.6,
              background: 'var(--bg)',
            }}>
              {g.narrative}
            </div>
          )}
          {/* Two-column trade breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {[
              { rid: rids[0], side: sideA, grade: atTime.grade_a, agedGrade: aged.grade_a, avgTier: atTime.avg_tier_a, agedTier: aged.avg_tier_a },
              { rid: rids[1], side: sideB, grade: atTime.grade_b, agedGrade: aged.grade_b, avgTier: atTime.avg_tier_b, agedTier: aged.avg_tier_b },
            ].map((col, i) => (
              <div key={i} style={{
                padding: '16px 18px',
                borderRight: i === 0 ? '1px solid var(--border)' : 'none',
                background: col.rid === winnerRid ? '#00ff870a' : 'transparent',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <GradeBadge grade={col.grade} size="lg" />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {col.side.manager || `Team ${i + 1}`}
                      {col.rid === winnerRid && <span style={{ marginLeft: 6, fontSize: 14 }}>🏆</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      Avg Tier {col.avgTier} at trade time
                    </div>
                  </div>
                </div>

                {/* Players received */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>
                    Received
                  </div>
                  {((col.side.received || {}).players || []).map((p, j) => <PlayerRow key={j} player={p} />)}
                  {((col.side.received || {}).picks || []).map((p, j) => <PickRow key={j} pick={p} />)}
                  {!((col.side.received || {}).players || []).length && !((col.side.received || {}).picks || []).length && (
                    <div style={{ color: 'var(--text3)', fontSize: 12 }}>Nothing recorded</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Aging analysis footer */}
          <div style={{
            padding: '12px 18px',
            background: 'var(--bg)',
            borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{ fontSize: 20 }}>{agingEmoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)' }}>
                Aging Analysis: {agingVerdict}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                Dynasty value shift since trade: {aged.aging_shift > 0 ? '+' : ''}{aged.aging_shift} GPA pts
                · Aged grades: {sideA.manager} <strong style={{ color: GRADE_COLOR[aged.grade_a] || '#888' }}>{aged.grade_a}</strong>
                {' '}vs {sideB.manager} <strong style={{ color: GRADE_COLOR[aged.grade_b] || '#888' }}>{aged.grade_b}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <GradeBadge grade={aged.grade_a} size="sm" />
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>↔</span>
              <GradeBadge grade={aged.grade_b} size="sm" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WaiverCard({ txn }) {
  const adds = Object.values(txn.adds || {});
  const drops = Object.values(txn.drops || {});
  const manager = txn.managers?.[0] || 'Unknown';
  const date = txn.status_updated
    ? new Date(txn.status_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
      padding: '10px 14px', marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 60 }}>{date}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', minWidth: 120 }}>{manager}</span>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {adds.map((p, i) => (
          <span key={i} style={{
            background: '#00ff8715', color: 'var(--green)', border: '1px solid #00ff8730',
            borderRadius: 4, fontSize: 11, padding: '2px 7px',
          }}>
            + {p.name} <span style={{ color: '#00ff8766' }}>({p.position})</span>
          </span>
        ))}
        {drops.map((p, i) => (
          <span key={i} style={{
            background: '#ff445515', color: '#ff6b6b', border: '1px solid #ff445530',
            borderRadius: 4, fontSize: 11, padding: '2px 7px',
          }}>
            − {p.name} <span style={{ color: '#ff445566' }}>({p.position})</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Transactions({ api: apiProp }) {
  const apiBase = apiProp || API;
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'trades' | 'waivers'
  const [historyMode, setHistoryMode] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTxns, setHistoryTxns] = useState([]);

  const fetchCurrent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${apiBase}/api/transactions`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setTxns(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/transactions/history`);
      const data = await r.json();
      setHistoryTxns(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  const displayed = filter === 'trades'
    ? txns.filter(t => t.type === 'trade')
    : filter === 'waivers'
    ? txns.filter(t => t.type !== 'trade')
    : txns;

  const tradeCount = txns.filter(t => t.type === 'trade').length;
  const waiverCount = txns.filter(t => t.type !== 'trade').length;

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-title">Transactions</div>
        <div className="page-subtitle">Recent league activity · Trades auto-graded by dynasty tier</div>
      </div>

      {/* Mode selector */}
      <div className="tab-bar">
        <button
          className={`tab-btn${!historyMode ? ' active' : ''}`}
          onClick={() => { setHistoryMode(false); fetchCurrent(); }}
        >
          Current Season
        </button>
        <button
          className={`tab-btn${historyMode ? ' active' : ''}`}
          onClick={() => { setHistoryMode(true); if (!historyTxns.length) fetchHistory(); }}
        >
          All-Time History
        </button>
      </div>

      {/* Filter tabs */}
      {!historyMode && (
        <div className="tab-bar tab-bar--scroll" style={{ marginTop: 0 }}>
          {[
            { key: 'all',     label: `All (${txns.length})` },
            { key: 'trades',  label: `Trades (${tradeCount})` },
            { key: 'waivers', label: `Waivers (${waiverCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              className={`tab-btn${filter === tab.key ? ' active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading && !historyMode && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading transactions & grading trades...</span>
        </div>
      )}

      {error && (
        <div style={{
          background: '#ff445511', border: '1px solid #ff445533',
          borderRadius: 8, padding: '12px 16px', color: '#ff6b6b', marginBottom: 16,
        }}>
          ❌ {error}
        </div>
      )}

      {/* All-time history mode */}
      {historyMode && (
        <>
          {historyLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <span>Fetching 5 seasons of trade history... (~15s first load)</span>
            </div>
          ) : historyTxns.length === 0 ? (
            <div className="empty-state-block">
              No historical trades found
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
                {historyTxns.length} trades graded across all seasons
              </div>
              {historyTxns.map((g, i) => {
                // Wrap history grades as pseudo-txn for TradeCard
                const pseudoTxn = {
                  transaction_id: g.transaction_id,
                  type: 'trade',
                  status_updated: g.trade_date,
                  managers: g.managers,
                  adds: {},
                  drops: {},
                  trade_info: {},
                  trade_grade: g,
                };
                return <TradeCard key={i} txn={pseudoTxn} />;
              })}
            </div>
          )}
        </>
      )}

      {/* Current season mode */}
      {!historyMode && !loading && !error && (
        <>
          {/* Trades section */}
          {(filter === 'all' || filter === 'trades') && tradeCount > 0 && (
            <div style={{ marginBottom: 28 }}>
              {filter === 'all' && (
                <div className="section-header">
                  <span className="section-header-title">Trades</span>
                </div>
              )}
              {txns
                .filter(t => t.type === 'trade')
                .map((txn, i) => <TradeCard key={i} txn={txn} />)
              }
            </div>
          )}

          {/* Waivers section */}
          {(filter === 'all' || filter === 'waivers') && waiverCount > 0 && (
            <div>
              {filter === 'all' && (
                <div className="section-header">
                  <span className="section-header-title">Waiver Wire</span>
                </div>
              )}
              {txns
                .filter(t => t.type !== 'trade')
                .map((txn, i) => <WaiverCard key={i} txn={txn} />)
              }
            </div>
          )}

          {displayed.length === 0 && (
            <div className="empty-state-block">
              No {filter !== 'all' ? filter : ''} transactions found recently.
            </div>
          )}
        </>
      )}
    </div>
  );
}
